import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const fired = useRef(false);

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage(t("auth.verifyTokenMissing")); return; }
    if (fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        // verify-email also sets httpOnly cookies on the response, so refresh()
        // will pick up the session.
        await refresh();
        setStatus("success");
        setMessage(`${t("auth.welcome")} ${data.name || data.email}!`);
        setTimeout(() => navigate("/dashboard", { replace: true }), 1800);
      } catch (e) {
        setStatus("error");
        setMessage(e.response?.data?.detail || t("auth.verifyGenericError"));
      }
    })();
  }, [token, navigate, refresh, t]);

  return (
    <div className="theme-swiss min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md text-center" data-testid="verify-email-page">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <BrandIcon size={40} />
          <div className="font-display font-bold text-xl text-slate-100">MachinePilot</div>
        </div>

        {status === "verifying" && (
          <div data-testid="verify-pending">
            <Loader2 size={48} className="text-blue-400 mx-auto animate-spin mb-4"/>
            <h1 className="font-display font-bold text-2xl text-slate-100">{t("auth.verifyTitlePending")}</h1>
            <p className="text-slate-400 mt-2">{t("auth.verifyBodyPending")}</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-5">
              <CheckCircle2 size={32} className="text-emerald-400"/>
            </div>
            <h1 className="font-display font-bold text-2xl text-slate-100">{t("auth.verifyTitleSuccess")}</h1>
            <p className="text-slate-400 mt-3">{message}</p>
            <p className="text-slate-500 text-xs mt-4">{t("auth.verifyRedirect")}</p>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center mb-5">
              <XCircle size={32} className="text-red-400"/>
            </div>
            <h1 className="font-display font-bold text-2xl text-slate-100">{t("auth.verifyTitleError")}</h1>
            <p className="text-slate-400 mt-3">{message}</p>
            <Link to="/login" className="mt-6 inline-block btn-primary px-5 py-2.5 rounded-md text-sm">{t("auth.backToLogin")}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
